import { useEffect, useMemo, useState } from 'react'
import { Check, UserPlus } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { Button } from '@/shared/ui/button'
import { Avatar } from '@/shared/ui/avatar'
import { Chip } from '@/shared/ui/chip'
import { showToast } from '@/shared/ui/toast'
import { useMyContacts } from '@/features/contactos/use-contacts-real'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { savedContactsList } from '@/shared/player-actions'
import { useSendInvites, useSentInvites } from './use-match-invites'
import type { MatchView } from './use-matches'

// Organizer → contacts invitation picker (spec/02). Sources: an optional
// "última vez" preselection (Repetir reinvite carries the previous roster)
// + the saved contacts list. Already-invited players show the sent state
// instead of a checkbox — re-inviting is possible but never accidental.

export interface InviteeSeed {
  uid: string
  name: string
}

export function ContactInviteSheet({
  match,
  open,
  onClose,
  preselect = [],
}: {
  match: MatchView
  open: boolean
  onClose: () => void
  preselect?: InviteeSeed[]
}) {
  const contacts = useMyContacts()
  const sent = useSentInvites(match.id, open)
  const sendInvites = useSendInvites()
  const [picked, setPicked] = useState<Set<string>>(new Set())

  const playing = useMemo(() => new Set(match.playerUids ?? []), [match.playerUids])
  const sentUids = useMemo(
    () => new Set((sent.data ?? []).map((i) => i.toUid)),
    [sent.data],
  )

  // Candidate rows: "última vez" seeds first, then active contacts (deduped),
  // never anyone already in the match.
  const rows = useMemo(() => {
    const seen = new Set<string>()
    const out: { uid: string; name: string; lastTime: boolean }[] = []
    for (const s of preselect) {
      if (playing.has(s.uid) || seen.has(s.uid)) continue
      seen.add(s.uid)
      out.push({ uid: s.uid, name: s.name, lastTime: true })
    }
    if (AUTH_BYPASS) {
      // Design walks: the mock saved-contacts list stands in for the join.
      for (const c of savedContactsList()) {
        if (seen.has(c.id)) continue
        seen.add(c.id)
        out.push({ uid: c.id, name: c.name, lastTime: false })
      }
      return out
    }
    for (const e of contacts.data ?? []) {
      const uid = e.contact.contactUid
      if (e.contact.status !== 'active' || playing.has(uid) || seen.has(uid)) continue
      seen.add(uid)
      out.push({ uid, name: e.profile?.name || 'Jugador', lastTime: false })
    }
    return out
  }, [preselect, contacts.data, playing])

  // Preselect the "última vez" players each time the sheet opens.
  useEffect(() => {
    if (open) setPicked(new Set(preselect.filter((s) => !playing.has(s.uid)).map((s) => s.uid)))
  }, [open, preselect, playing])

  const toggle = (uid: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })

  const onSend = async () => {
    const toUids = [...picked]
    await sendInvites.mutateAsync({ match, toUids })
    showToast(toUids.length === 1 ? 'Invitación enviada' : `${toUids.length} invitaciones enviadas`)
    onClose()
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Invitar a jugadores">
      <div className="flex flex-col gap-3 pb-2">
        {contacts.isError ? (
          /* A failed read is not an empty contact list. Without this branch the
             sheet told a player with saved contacts to go and save some — and
             pushed them to WhatsApp instead of the thing they came here to do. */
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            No pudimos cargar tus contactos.{' '}
            <button
              type="button"
              onClick={() => void contacts.refetch()}
              className="font-semibold text-[color:var(--accent)]"
            >
              Reintentar
            </button>
          </p>
        ) : rows.length === 0 ? (
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            Aún no tienes contactos que invitar — guarda jugadores desde su ficha y aparecerán
            aquí. Mientras tanto, compártelo por WhatsApp.
          </p>
        ) : (
          <>
            <div className="max-h-[45dvh] overflow-y-auto -mx-1 px-1 flex flex-col">
              {rows.map((r) => {
                const invited = sentUids.has(r.uid)
                return (
                  <button
                    key={r.uid}
                    type="button"
                    onClick={() => toggle(r.uid)}
                    className="flex items-center gap-3 min-h-[var(--tap)] px-2 rounded-[var(--radius)] text-left hover:bg-[var(--line-soft)]"
                  >
                    <Avatar name={r.name} size={34} />
                    <span className="flex-1 min-w-0 truncate font-medium">{r.name}</span>
                    {r.lastTime && <Chip>Última vez</Chip>}
                    {invited && !picked.has(r.uid) ? (
                      <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                        Invitado
                      </span>
                    ) : (
                      <span
                        className={`h-[32px] w-[32px] shrink-0 rounded-full border inline-flex items-center justify-center ${
                          picked.has(r.uid)
                            ? 'bg-[var(--accent)] border-[var(--accent)] text-[color:var(--accent-fg)]'
                            : 'border-[var(--line-strong)]'
                        }`}
                      >
                        {picked.has(r.uid) && <Check size={14} />}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
            <Button
              disabled={picked.size === 0 || sendInvites.isPending}
              onClick={() => void onSend()}
            >
              <UserPlus size={17} />
              {picked.size === 0 ? 'Invitar' : picked.size === 1 ? 'Invitar a 1' : `Invitar a ${picked.size}`}
            </Button>
            <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
              Recibirán un aviso con el partido — se unen con un toque.
            </p>
          </>
        )}
      </div>
    </BottomSheet>
  )
}
